import { inject, computed, ref, watch } from 'vue';
import { isServer } from '@sitecore-jss/sitecore-jss';
import { routeChangeStatus } from '../router';
import { getSegmentName } from '../utils/getSegmentName';

// Singleton instance
let instance = null;

/**
 * Returns standardized base properties for data layer
 *
 * @returns {Object} Computed properties for data layer (singleton)
 */
export const useBaseDataLayerVariables = () => {
  if (instance) return instance;

  const jssStore = inject('jssStore');
  const sitecoreContext = ref(jssStore?.sitecoreContext() || {});

  const updateSitecoreContext = () => {
    sitecoreContext.value = jssStore?.sitecoreContext() || {};
  };

  watch(routeChangeStatus, () => updateSitecoreContext(), { immediate: true });

  const pageURL = computed(() => {
    if (isServer())
      return {
        previous: '',
        current: '',
      };

    const previous = routeChangeStatus.previousInternalRoute
      ? window.location.origin + routeChangeStatus.previousInternalRoute
      : '';
    const current = routeChangeStatus.currentInternalRoute || '';

    return {
      previous: previous || document.referrer || '',
      current: current || window.location.href || '',
    };
  });

  const currentUrl = computed(() => {
    const canonicalUrl = sitecoreContext.value.canonicalUrl || '/';
    const windowValue = window.location.href.split('?')[0];
    return !isServer() ? windowValue : canonicalUrl;
  });

  // Standard base properties for all events
  const baseProps = computed(() => {
    const {
      venueName = '',
      venueId = '',
      brandName = '',
      segment = '',
      isHotel = true,
      sports = false,
      division,
    } = sitecoreContext.value;
    return {
      brand_name: brandName, // Was pubBrandName
      brand_segment: getSegmentName(segment, brandName, venueId), // Was pubSegmentName
      division: division || 'No Division', // New field
      house_name: venueName, // Was part of pubName
      house_id: venueId, // Was part of pubName
      hotel_flag: isHotel, // Was Hotel (Yes/No)
      sports_flag: sports, // Was Sports (Yes/No)
      previous_page_url: pageURL.value.previous, // New field
    };
  });

  // Additional properties for page view events
  const pageProps = computed(() => {
    return {
      full_url: currentUrl.value,
    };
  });

  // Link properties for tracking clicks
  const linkProps = computed(() => {
    const { venueOrigin = '' } = sitecoreContext.value;
    return {
      link_page_path: pageURL.value.current,
      link_domain: venueOrigin,
    };
  });

  // Item-level properties for ecommerce events
  const itemProps = computed(() => {
    const {
      venueId = '',
      segment = '',
      venueName = '',
      siteVirtualFolder = '',
    } = sitecoreContext.value;
    const pathSegments = siteVirtualFolder?.split('/') || [];
    return {
      item_id: venueId,
      item_brand: segment,
      affiliation: venueName,
      item_category2: pathSegments[1] || '',
      item_category3: pathSegments[2] || '',
    };
  });

  // Promotion item properties for promotion related events
  const promoItemProps = computed(() => ({
    location_id: currentUrl.value,
  }));

  instance = {
    baseProps,
    pageProps,
    linkProps,
    itemProps,
    promoItemProps,
    updateSitecoreContext,
  };
  return instance;
};
